import type { FemaEnvironment } from 'types/fema.types';
import { FEMA_ENVIRONMENT_CLASSES, type FemaEnvironmentClass } from 'types/femaTenant.types';

/**
 * RescAlert (Mike, 2026-09-25, card FE-01; multi-COG contract §1 and §2): the FEMA environment
 * select in the IPAWS banner. The operator and the client see the three FEMA environment classes.
 *
 * The selection stays in the Alert Creator draft as `femaEnvironment`. The CAP payload builders do
 * not read it. A new send carries it as the multipart field `environment` beside `data`, but only
 * when `GET /fusion/v2/fema/meta` serves `environments[]`. An older backend serves no
 * `environments[]`: then the select enables only the class that the service serves, and the send
 * carries no class. Update and Cancel never carry a class.
 */
export type FemaEnvironmentOption = {
  /** The class token that the select stores and that the wire carries. */
  value: FemaEnvironmentChoice;
  /** The service class that this option names (`FemaMeta.environment`). */
  environment: FemaEnvironment;
  /** The option text. */
  label: string;
  /** The short name in the "The backend serves … only." sentence. */
  shortLabel: string;
  /** True for the class that can reach the public. The select marks it as LIVE PRODUCTION. */
  isProduction: boolean;
  /** The react-bootstrap badge variant for this class (the FEMA list uses the same variants). */
  badgeVariant: 'secondary' | 'info' | 'danger';
  /** The badge text. */
  badgeLabel: string;
  /**
   * The label on the FEMA credential page. Credential management keeps the multi-COG contract §0
   * labels (Mike, 2026-09-26: the team's FEMA labels win everywhere but credential management).
   */
  credentialLabel: string;
};

/**
 * The ONE class list (`FEMA_ENVIRONMENT_CLASSES`, `types/femaTenant.types.ts`). This name is an
 * alias of that array. The select tokens are the wire tokens.
 */
export const FEMA_ENVIRONMENT_CHOICES = FEMA_ENVIRONMENT_CLASSES;
export type FemaEnvironmentChoice = FemaEnvironmentClass;

/** The three options, in the order that the select shows them. */
export const FEMA_ENVIRONMENT_OPTIONS: readonly FemaEnvironmentOption[] = [
  {
    value: 'tdl',
    environment: 'TDL',
    label: 'Test Development Lab (TDL) / Staging',
    shortLabel: 'Test Development Lab (TDL) / Staging',
    isProduction: false,
    badgeVariant: 'secondary',
    badgeLabel: 'TDL',
    credentialLabel: 'TDL / Staging',
  },
  {
    value: 'demo',
    environment: 'DEMO',
    label: 'IPAWS DEMO / TEST',
    shortLabel: 'IPAWS DEMO / TEST',
    isProduction: false,
    badgeVariant: 'info',
    badgeLabel: 'DEMO',
    credentialLabel: 'Test / Demo',
  },
  {
    value: 'production',
    environment: 'PRODUCTION',
    label: 'IPAWS LIVE',
    shortLabel: 'IPAWS LIVE',
    isProduction: true,
    badgeVariant: 'danger',
    badgeLabel: 'LIVE PRODUCTION',
    credentialLabel: 'LIVE PRODUCTION',
  },
];

export const FEMA_ENVIRONMENT_SELECT_LABEL = 'Environment:';
/*FEMA environment and COG'*/
/** The select text while the meta loads or after the meta fails. */
export const FEMA_ENVIRONMENT_NOT_LOADED = 'FEMA environment not loaded';
/** The finding for a stored class that the service does not serve or that cannot send. */
export const FEMA_ENVIRONMENT_NOT_AVAILABLE = 'The selected FEMA environment is not available.';
/** The option suffix for a class that the workplace does not hold. */
export const FEMA_ENVIRONMENT_NOT_HELD = 'not held by this workplace';
/** The warning beside the LIVE PRODUCTION badge when production is the selected class. */
export const FEMA_ENVIRONMENT_LIVE_WARNING =
  'A send goes to the public through the authorized FEMA channels.';
/** The help text while an Update locks the select. */
export const FEMA_ENVIRONMENT_LOCKED =
  'An Update goes to the FEMA environment of the original message. You cannot change it.';
/** The finding when the `/jurisdictions` answer names a class other than the class asked for. */
export const FEMA_JURISDICTIONS_CLASS_MISMATCH =
  'The authorized areas came back for a different FEMA environment. Reload the page.';
